/* ═══════════════════════════════════════════════════════════════
   Module draft — the pure rules behind the Module Builder's shared draft
   (docs/SYNC_PLAN.md §2.5, D14). A module's definition is edited in a live
   shared document; the published `chunks` only move on Publish. Everything
   here is a plain function so the chip, the seed choice and the Publish gate
   are tested without a socket.
   ═══════════════════════════════════════════════════════════════ */

import type { ModuleChunk } from '../../shared/spec-modules';
import type { CollabStatus } from './collabState';

/** The one chip of a module: Published (no draft) / Pending publish (a draft
 *  differs) / Saving… / Offline — plus Connecting… while the document opens. */
export type ModuleChipState = 'published' | 'pending' | 'saving' | 'connecting' | 'offline';

export function deriveModuleChipState(input: {
  status: CollabStatus;
  unsyncedChanges: number;
  mirrorPending: boolean;
  /** The shared document has synced AND been seeded (the canvas is bound). */
  ready: boolean;
  /** A draft exists that differs from the published module. */
  pending: boolean;
}): ModuleChipState {
  if (input.status === 'offline') return 'offline';
  if (input.status === 'connecting' || !input.ready) return 'connecting';
  if (input.unsyncedChanges > 0 || input.mirrorPending) return 'saving';
  return input.pending ? 'pending' : 'published';
}

export function moduleChipLabel(state: ModuleChipState): string {
  switch (state) {
    case 'published':
      return 'Published';
    case 'pending':
      return 'Pending publish';
    case 'saving':
      return 'Saving…';
    case 'connecting':
      return 'Connecting…';
    default:
      return 'Offline';
  }
}

/** What an empty shared document is seeded from: the server-held draft when
 *  one exists (it outlives a closed tab), else the published definition. */
export function moduleSeedChunks(
  published: ModuleChunk[] | null | undefined,
  draft: ModuleChunk[] | null | undefined
): ModuleChunk[] {
  return draft ?? published ?? [];
}

/** Does this definition differ from the published one? */
export function moduleDraftDiffers(
  chunks: ModuleChunk[],
  published: ModuleChunk[] | null | undefined
): boolean {
  return JSON.stringify(chunks) !== JSON.stringify(published ?? []);
}

/** May the Publish button run? Needs the key, a valid structure, a draft that
 *  differs, a name, and nothing already in flight. */
export function canPublishModule(input: {
  mayPublish: boolean;
  valid: boolean;
  pending: boolean;
  ready: boolean;
  busy: boolean;
  hasName: boolean;
}): boolean {
  return (
    input.mayPublish && input.valid && input.pending && input.ready && !input.busy && input.hasName
  );
}

function whoIsIn(peers: Array<{ name: string }>): string {
  const n = peers.length - 1;
  return n === 0
    ? `${peers[0].name} is`
    : `${peers[0].name} and ${n} other${n === 1 ? '' : 's'} are`;
}

/** The Publish confirm: names who else is in the draft. */
export function modulePublishBody(peers: Array<{ name: string }>, nextVersion: number): string {
  const head = `Publish this draft as v${nextVersion}? Every new insert gets it, and every spec already using the module is offered the update.`;
  if (!peers.length) return head;
  const first = `${peers[0].name}'s`;
  const more = peers.length - 1;
  const names = more ? `${first} and ${more} other${more === 1 ? '' : 's'}'` : first;
  return `${head} ${whoIsIn(peers)} also in this draft, and it will close for them. Publishes everyone's edits, including ${names}.`;
}

/** The Discard confirm: who loses the draft. */
export function moduleDiscardBody(peers: Array<{ name: string }>): string {
  if (!peers.length) {
    return 'Discard this draft for everyone? Nobody else is editing it right now. The published module is not touched.';
  }
  return `Discard this draft for everyone? ${whoIsIn(peers)} editing it. The published module is not touched. This cannot be undone.`;
}
